// ============================================================
//  src/pages/inbox/InboxBotPage.js
//
//  Hosts the existing InboxTab component for one specific bot,
//  now reached from the sidebar's "Inbox" item rather than nested
//  inside the bot's own tab bar.
//
//  LAYOUT APPROACH: this page is pinned with position:fixed directly
//  to explicit viewport coordinates (top/left/right/bottom), rather
//  than relying on percentage/flex heights cascading correctly down
//  through AppLayout -> .app-main -> .page-content -> this component.
//  That flex-height chain repeatedly proved fragile in this project
//  (a single missing min-height:0 or an overridden className anywhere
//  in the chain silently breaks containment for every descendant), so
//  instead of continuing to patch that chain, this component sizes
//  itself purely against the viewport. A position:fixed element's
//  containing block is the viewport itself (unless an ancestor uses
//  transform/filter/perspective, which nothing in AppLayout does), so
//  this is correct regardless of whatever height AppLayout's own
//  wrapper divs end up resolving to.
// ============================================================
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getBot } from '../../api/client';
import InboxTab from '../bots/tabs/InboxTab';
import './InboxPages.css';

const SIDEBAR_WIDTH = 260; // must match --sidebar-width in src/index.css
const TOPBAR_HEIGHT  = 58;  // must match .topbar { height: 58px } in AppLayout.css
const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

export default function InboxBotPage() {
  const { bId }  = useParams();
  const navigate = useNavigate();
  const [bot, setBot]         = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  const loadBot = useCallback(() =>
    getBot(bId)
      .then(res => { if (res.data?.success) setBot(res.data.bot); else toast.error('Bot not found'); })
      .catch(() => toast.error('Failed to load bot'))
      .finally(() => setLoading(false))
  , [bId]);

  useEffect(() => { loadBot(); }, [loadBot]);

  // Every state this component can render shares the exact same
  // viewport-pinned geometry, so a bare div gets it too — no chrome
  // should ever be able to scroll out of view, loading or not.
  const fixedShellStyle = {
    position: 'fixed',
    top: isMobile ? TOPBAR_HEIGHT : 0,
    left: isMobile ? 0 : SIDEBAR_WIDTH,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    background: '#fff',
    boxSizing: 'border-box',
    zIndex: 5,
  };

  if (loading) {
    return (
      <div style={fixedShellStyle}>
        <div className="inbox-picker-loading" style={{ flex: 1 }}>
          <span className="spinner spinner-dark" /> Loading bot…
        </div>
      </div>
    );
  }

  if (!bot) {
    return (
      <div style={{ ...fixedShellStyle, padding: 40, overflow: 'auto' }}>
        <p style={{ color: 'var(--text-muted)' }}>Bot not found.</p>
        <button className="btn btn-outline" onClick={() => navigate('/inbox')}>← Back to Inbox</button>
      </div>
    );
  }

  return (
    <div style={fixedShellStyle}>
      <div className="inbox-page-breadcrumb" style={{ flexShrink: 0 }}>
        <Link to="/inbox" className="breadcrumb-back">← Inbox</Link>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-cur">{bot.bName}</span>
        <Link to={`/bots/${bId}`} className="inbox-bot-page-manage-link">Manage bot →</Link>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <InboxTab bId={bId} />
      </div>
    </div>
  );
}